import React, { useState, useEffect } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { getter } from "@progress/kendo-react-common";
import { useDispatch, useSelector } from "react-redux";
import AddUser from "./AddUser";
import { useKendoGridHandlers } from "../../CustomHooks/useKendoGridHandlers";
import { loadAllUserGridData } from "../../handlers/UserGridhandler";
import useReloadButton from "../../CustomHooks/useReloadButton";
import useHandlePageChange from "../../CustomHooks/useHandlePageChange";
import { setCurrentPage } from "../../store/reducers/UserFilterSlice";
import { setCurrentTab } from "../../store/reducers/SharedLayoutDataSlice";

const DATA_ITEM_KEY = "EMAIL";
const SELECTED_FIELD = "selected";

const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO_USER = {
  action: "LOADALLSUPPORTUSERS",
  className: "USER",
  pageSize: 20,
  currentPageNumber: 1,
  sortName: "FIRST_NAME",
  sortOrder: "desc",
  gridHeight: "300px",
};

const PAGER_SETTINGS = {
  info: true,
  type: "input",
  previousNext: true,
  pageSize: 4,
  refresh: true,
};

const KendoUserGrid = React.memo((props) => {
  const dispatch = useDispatch();

  const userData = useSelector((state) => state.userFilter);

  const [popupUser, setPopupUser] = useState(false);
  const [currUser, setCurUser] = useState();

  const [gridData, setGridData] = useState([]);
  const [total, setTotal] = useState(0);
  const [reloadToggle, setReloadToggle] = useState(false);

  const [gridState, setGridState] = useState({
    pageNumber: DEFAULT_GRID_INFO_USER.currentPageNumber,
    pageSize: DEFAULT_GRID_INFO_USER.pageSize,
    sortName: DEFAULT_GRID_INFO_USER.sortName,
    sortOrder: DEFAULT_GRID_INFO_USER.sortOrder,
    sort: [
      {
        field: DEFAULT_GRID_INFO_USER.sortName,
        dir: DEFAULT_GRID_INFO_USER.sortOrder?.toLowerCase() || "asc",
      },
    ],
  });

  const [selectedState, setSelectedState] = React.useState();

  const HandlePageChange = useHandlePageChange(setGridState, setCurrentPage);

  const { HandleDataStateChange } = useKendoGridHandlers(setGridState);

  useEffect(() => {
    loadAllUserGridData(userData, gridState).then((response) => {
      setGridData(response.rows);
      setTotal(response.total);
    });
  }, [userData, gridState, reloadToggle, props]);

  useReloadButton(setReloadToggle);

  const SelectedContentLength = () => {
    const selectedText = window.getSelection().toString();
    return selectedText.length > 0;
  };

  const onSelectionChange = React.useCallback(
    (event) => {
      const check = SelectedContentLength();
      if (!check) {
        const newSelectedState = getSelectedState({
          event,
          selectedState,
          dataItemKey: DATA_ITEM_KEY,
        });

        setSelectedState(newSelectedState);
        const currentColumn = event.target._columns[event.endColIndex].field;
        if (currentColumn === "EMAIL") {
          ShowUserDetail(event.dataItems[event.endRowIndex]);
        }
      }
    },
    [selectedState]
  );

  const EmailField = (props) => (
    <td>
      <p
        style={{
          textAlign: "left",
          color: "#0b70b8",
          cursor: "pointer",
          margin: "0px",
        }}
        className="hover:underline link pl-3"
        onClick={() => ShowUserDetail(props)}
      >
        {props.dataItem.EMAIL}
      </p>
    </td>
  );

  const ActiveField = (props) => {
    if (props.dataItem.ACTIVE === "Y") {
      return <td className="Comments30 pl-4">Yes</td>;
    }
    return <td className="Comments30 pl-4">No</td>;
  };

  const ActivatedTypeField = (props) => {
    if (
      props.dataItem.ACCOUNT_ACTIVATED === "Y" ||
      props.dataItem.ACCOUNT_ACTIVATED === "y" ||
      props.dataItem.ACCOUNT_ACTIVATED === ""
    ) {
      return <td className="Comments30 pl-4">Yes</td>;
    }
    return <td className="Comments30 pl-4">No</td>;
  };

  const ShowUserDetail = (props) => {
    setCurUser(props.dataItem);
    setPopupUser(true);
  };

  const HandleSetUser = (childData) => {
    if (childData.reloadGrid === true) {
      setReloadToggle(!reloadToggle);
    }
    setPopupUser(false);
  };

  const TypeField = (props) => {
    if (props.dataItem.TYPE === "SP" || props.dataItem.STATUS === "sp") {
      return <td className="Comments30 pl-5">Sales Person</td>;
    }
    if (props.dataItem.TYPE === "S" || props.dataItem.TYPE === "s") {
      return <td className="Comments30 pl-5">Support</td>;
    }
    if (props.dataItem.TYPE === "M" || props.dataItem.TYPE === "m") {
      return <td className="Comments30 pl-5">Implementation Manager</td>;
    }
    if (props.dataItem.TYPE === "I" || props.dataItem.TYPE === "i") {
      return <td className="Comments30 pl-5">Implementation Team</td>;
    }
    if (props.dataItem.TYPE === "CS" || props.dataItem.TYPE === "cs") {
      return <td className="Comments30 pl-5">Client Services</td>;
    }
    if (props.dataItem.TYPE === "CSM" || props.dataItem.TYPE === "csm") {
      return <td className="Comments30 pl-5">Client Services</td>;
    }
    if (props.dataItem.TYPE === "D" || props.dataItem.TYPE === "d") {
      return <td className="Comments30 pl-5">Internal Teams</td>;
    }
    return (
      <td className="Comments30 pl-5">
        {props.dataItem.TYPE === "undefined" ? "" : props.dataItem.TYPE}
      </td>
    );
  };

  return (
    <>
      {popupUser && (
        <AddUser ParentCallBack={HandleSetUser} supportUser={currUser} />
      )}

      <div>
        <Tooltip openDelay={100} position="right" anchorElement="target">
          <Grid
            style={{
              height: "calc(100vh - 181px)",
            }}
            {...gridState}
            scrollable="scrollable"
            total={total}
            currentPage
            pageSize={gridState.pageSize}
            filterable={false}
            sort={gridState.sort}
            sortable={{
              mode: "single",
              allowUnsort: false,
            }}
            skip={(userData.currentPage - 1) * gridState.pageSize}
            selectedField={SELECTED_FIELD}
            data={gridData.map((item) => ({
              ...item,
              [SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
            }))}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
            pageable={PAGER_SETTINGS}
            onPageChange={HandlePageChange}
            onDataStateChange={HandleDataStateChange}
            onSelectionChange={onSelectionChange}
          >
            <GridColumn
              title="First Name"
              field="FIRST_NAME"
              className="pl-4"
              sortable
            />
            <GridColumn
              title="Last Name"
              field="LAST_NAME"
              className="pl-4"
              sortable
            />
            <GridColumn
              title="Email"
              field="EMAIL"
              cell={EmailField}
              sortable
            />
            <GridColumn
              title="Type"
              className="textWrap"
              field="TYPE"
              cell={TypeField}
            />
            <GridColumn title="Active" field="ACTIVE" cell={ActiveField} />
            <GridColumn
              title="Account Activated"
              field="ACCOUNT_ACTIVATED"
              cell={ActivatedTypeField}
            />
          </Grid>
        </Tooltip>
      </div>
    </>
  );
});
export default KendoUserGrid;
